// Where a nav row that remembers (`NavItem.remembers`: Cases, Agents) goes:
// back to what was last open under it on this device, a detail or the list.
// The old web did this (2026-10-07, the person: "when I press the sidebar
// again, open the detail I had open last; if the list was last, the list").
//
// Promise:
// - Every way of going there takes the same path: the sidebar's row, the
//   folded column's, the phone's Dock and its menu, a native app's Dock (they
//   all read the menu's `to`, shell/outside/source.tsx).
// - From under the row's own path (the list or one of its details) the row
//   goes to the list: pressing it again comes back up.
// - What is remembered is the path last shown under the row, without its
//   search (`?at=` and the like are for the jump that opened it). A page that
//   makes a new thing (`<to>/new`) counts as the list.
// - Kept on the device, one value per row. A detail gone since is told by the
//   detail, as a link to it would be.
import { useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  defineDeviceSetting,
  useDeviceSetting,
} from "../device/device-setting";

type Places = Readonly<Record<string, string>>;

const parse = (raw: string | null): Places => {
  if (!raw) return {};
  try {
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    return Object.fromEntries(
      Object.entries(v).filter(
        (e): e is [string, string] =>
          typeof e[1] === "string" && e[1].startsWith(`${e[0]}/`),
      ),
    );
  } catch {
    return {};
  }
};

export const navPlacesSetting = defineDeviceSetting<Places>({
  key: "trameria-nav-last",
  parse,
  serialize: (v) => JSON.stringify(v),
});

const under = (path: string, to: string) =>
  path === to || path.startsWith(`${to}/`);

/** The path to keep for the row at `to` while `path` is shown ("" for its list). */
export function placeUnder(to: string, path: string): string | null {
  if (!under(path, to)) return null;
  const rest = path.slice(to.length).replace(/\/+$/, "");
  return rest === "" || rest === "/new" ? "" : `${to}${rest}`;
}

/** Where the row at `to` goes from `path`, with what was kept. */
export function navTarget(to: string, path: string, places: Places): string {
  if (under(path, to)) return to;
  return places[to] ?? to;
}

/**
 * Keeps what is shown under the rows at `remembering` and returns where a row
 * at `to` goes now (rows that do not remember go to their `to`).
 */
export function useNavTargets(
  remembering: readonly string[],
): (to: string) => string {
  const path = useRouterState({
    select: (s) => (s.resolvedLocation ?? s.location).pathname,
  });
  const [places, setPlaces] = useDeviceSetting(navPlacesSetting);
  const at = remembering.find((to) => under(path, to));
  const kept = at === undefined ? null : placeUnder(at, path);
  // frontend-development: not a fetch; keeps the place shown on the device
  useEffect(() => {
    if (at === undefined || kept === null) return;
    const current = navPlacesSetting.read();
    if ((current[at] ?? "") === kept) return;
    const next = { ...current };
    if (kept === "") delete next[at];
    else next[at] = kept;
    setPlaces(next);
  }, [at, kept, setPlaces]);
  return (to) => (remembering.includes(to) ? navTarget(to, path, places) : to);
}
